export interface MetadataVerificationResult {
  ok: boolean;
  observedCount?: number;
  expectedCount: number;
  attempts: number;
  settling: boolean;
}

/**
 * Verifies project chapterCount after a metadata write.
 * Callers must supply readChapterCount using an authoritative server read
 * (for example Firestore getDocFromServer) — not cache-backed getDoc().
 */
export async function verifyProjectChapterCountWithRetry(params: {
  readChapterCount: () => Promise<number | undefined>;
  expectedCount: number;
  maxAttempts?: number;
  initialDelayMs?: number;
}): Promise<MetadataVerificationResult> {
  const maxAttempts = params.maxAttempts ?? 4;
  const initialDelayMs = params.initialDelayMs ?? 250;
  let delayMs = initialDelayMs;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const observed = await params.readChapterCount();
    if (typeof observed === 'number' && observed >= params.expectedCount) {
      return {
        ok: true,
        observedCount: observed,
        expectedCount: params.expectedCount,
        attempts: attempt,
        settling: attempt > 1,
      };
    }

    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      delayMs = Math.min(delayMs * 2, 2000);
    }
  }

  const finalObserved = await params.readChapterCount();
  return {
    ok: typeof finalObserved === 'number' && finalObserved >= params.expectedCount,
    observedCount: finalObserved,
    expectedCount: params.expectedCount,
    attempts: maxAttempts,
    settling: false,
  };
}

export function dedupeChaptersByNumber<T extends { id: number }>(chapters: T[]): T[] {
  const byNumber = new Map<number, T>();
  for (const chapter of chapters) {
    if (!chapter?.id || chapter.id < 1) continue;
    byNumber.set(chapter.id, chapter);
  }
  return Array.from(byNumber.values()).sort((a, b) => a.id - b.id);
}
